import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { type SyntheticEvent, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { OrderBy } from '../../../../components/Hooks/useList.types';
import RHFAutocomplete from '../../../../components/ReactHookForm/RHFAutocomplete';
import type { PaginatedApiFetcher } from '../../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';
import LangEnum from '../../../../lang/LangEnum';
import { onScrollEndReloadForUseInfinityList } from '../../../../utils/generics';
import { useTranslation } from '../../../translation/TranslationContext';
import useCatalogBatchAddEntityModal from './hooks/useCatalogBatchAddEntityModal';

interface CatalogBatchAddEntityModalProps<T extends { id: string }> {
  open: boolean;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
  queryKeyBase: string;
  fetchFn: PaginatedApiFetcher<T>;
  searchParam: string;
  defaultOrderBy: OrderBy;
  titleKey: LangEnum;
  descriptionKey: LangEnum;
  searchEntityKey: LangEnum;
  getOptionLabel: (option: T) => string;
}

interface FormData<T> {
  items: T[];
}

const CatalogBatchAddEntityModal = <T extends { id: string }>({
  open,
  onClose,
  onConfirm,
  queryKeyBase,
  fetchFn,
  searchParam,
  defaultOrderBy,
  titleKey,
  descriptionKey,
  searchEntityKey,
  getOptionLabel,
}: CatalogBatchAddEntityModalProps<T>) => {
  const { translate } = useTranslation();
  const form = useForm<FormData<T>>({ defaultValues: { items: [] } });

  const { items, setSearch, hasNextPage, fetchNextPage } = useCatalogBatchAddEntityModal<T>({
    queryKeyBase,
    fetchFn,
    searchParam,
    defaultOrderBy,
    isOpen: open,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: form.reset is stable
  useEffect(() => {
    if (open) {
      form.reset({ items: [] });
    }
  }, [open]);

  const handleSubmit = form.handleSubmit((data) => {
    onConfirm(data.items.map((item) => item.id));
  });

  return (
    <FormProvider {...form}>
      <Modal
        isOpen={open}
        size="standard"
        header={{ title: translate(titleKey) }}
        onClose={onClose}
        footer={{
          primaryAction: {
            label: translate(LangEnum.GLOBAL_SAVE),
            onClick: handleSubmit,
          },
          secondaryAction: {
            label: translate(LangEnum.GLOBAL_CANCEL_LABEL),
            onClick: onClose,
          },
        }}
      >
        <Stack gap="24px">
          <Typography variant="body1" color="text.secondary">
            {translate(descriptionKey)}
          </Typography>
          <RHFAutocomplete<T, FormData<T>>
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            name="items"
            control={form.control}
            options={items}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChangeDelayed={setSearch}
            onBlur={() => setSearch('')}
            label={translate(LangEnum.CATALOGS_BULK_ADD_ENTITIES_SEARCH_LABEL, {
              entity: translate(searchEntityKey),
            })}
            filterOptions={(options: T[]) => options}
            sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'wrap', overflow: 'visible' } }}
            slotProps={{
              listbox: {
                onScroll: (event: SyntheticEvent) => {
                  onScrollEndReloadForUseInfinityList({
                    event,
                    hasNextPage,
                    loadMoreRows: fetchNextPage,
                  });
                },
              },
            }}
          />
        </Stack>
      </Modal>
    </FormProvider>
  );
};

export default CatalogBatchAddEntityModal;
